import React, { useState } from 'react';
import { Trash2, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDeleteUserMutation } from '@/store/api/deleteUserApi';

export function DeleteUserPage() {
  const [userId, setUserId] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [deleteUser, { isLoading }] = useDeleteUserMutation();

  const handleDelete = async () => {
    if (!userId.trim()) return;
    setResult(null);
    try {
      const res = await deleteUser(userId.trim()).unwrap();
      setResult({ type: 'success', message: res.message || 'User deleted successfully.' });
      setUserId('');
      setConfirmed(false);
    } catch (err: any) {
      const msg =
        err?.data?.message || err?.error || 'Failed to delete user. Please check the User ID.';
      setResult({ type: 'error', message: msg });
      setConfirmed(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-[linear-gradient(to_right,#082261,#34065c)] p-6 text-white">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-full p-2">
                <Trash2 className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-xl font-semibold font-spectral">Delete User</h1>
                <p className="text-white/70 text-sm mt-0.5">Permanently remove a user account</p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 space-y-5">
            {/* Warning banner */}
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-700">
                This action is <span className="font-semibold">irreversible</span>. The user and all
                associated data will be permanently deleted.
              </p>
            </div>

            {/* Input */}
            <div className="space-y-2">
              <Label htmlFor="userId" className="text-sm font-medium text-gray-700">
                User ID
              </Label>
              <Input
                id="userId"
                placeholder="e.g. 69b79d70507735222f2b819a"
                value={userId}
                onChange={(e) => {
                  setUserId(e.target.value);
                  setConfirmed(false);
                  setResult(null);
                }}
                className="font-mono text-sm"
              />
            </div>

            {/* Confirmation checkbox */}
            {userId.trim() && (
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="w-4 h-4 accent-[#082261] rounded"
                />
                <span className="text-sm text-gray-600">
                  I understand this will permanently delete user{' '}
                  <span className="font-mono font-semibold text-gray-800 break-all">
                    {userId.trim()}
                  </span>
                </span>
              </label>
            )}

            {/* Result banner */}
            {result && (
              <div
                className={`flex items-start gap-3 rounded-lg p-4 ${
                  result.type === 'success'
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-red-50 border border-red-200'
                }`}
              >
                {result.type === 'success' ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
                )}
                <p
                  className={`text-sm ${
                    result.type === 'success' ? 'text-green-700' : 'text-red-700'
                  }`}
                >
                  {result.message}
                </p>
              </div>
            )}

            {/* Delete button */}
            <button
              onClick={handleDelete}
              disabled={!userId.trim() || !confirmed || isLoading}
              className="
                w-full py-2.5 px-4 rounded-lg font-semibold font-spectral text-sm uppercase
                transition-all duration-300 ease-in-out
                bg-red-600 text-white
                hover:bg-red-700
                disabled:opacity-40 disabled:cursor-not-allowed
                flex items-center justify-center gap-2
              "
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    />
                  </svg>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  Delete User
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
